import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Loader2,
  MapPin,
  Briefcase,
  DollarSign,
  Users,
  Heart,
  Zap,
  Globe,
  TrendingUp,
  Search,
  User,
  Upload,
  Wallet,
  GraduationCap,
  Clock,
} from "lucide-react";
import "./Home.css";

function Home({ setAnalysisResult }) {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [employmentStatus, setEmploymentStatus] = useState("employed");

  const [formData, setFormData] = useState({
    full_name: "",
    nationality: "",
    current_country: "",
    occupation: "",
    yoe: "",
    skills: "",
    monthly_income: "",
    savings_amount: "",
    has_family: false,
    family_size: 1,
    priorities: [],
  });

  // --- FETCH SAVED PROFILE ON LOAD ---
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) return;

        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/analysis/profile`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (response.data) {
          const p = response.data;
          setFormData({
            full_name: p.full_name || "",
            nationality: p.nationality || "",
            current_country: p.current_country || "",
            occupation: p.occupation || "",
            yoe: p.yoe || "",
            skills: p.skills ? p.skills.join(", ") : "",
            monthly_income: p.monthly_income || "",
            savings_amount: p.savings_bracket || "",
            has_family: p.has_family || false,
            family_size: p.family_size || 1,
            priorities: p.priorities || [],
          });

          // Auto-set employment status
          if (p.monthly_income === 0) setEmploymentStatus("unemployed");

          // Skip to form if data exists
          setStep(2);
        }
      } catch (err) {
        console.log("No saved profile found or error:", err);
      }
    };
    fetchProfile();
  }, []);

  // --- CV UPLOAD ---
  const handleFileUpload = async (file) => {
    if (!file) return;
    setUploading(true);

    const data = new FormData();
    data.append("file", file);

    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/analysis/upload-cv`,
        data,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const parsed = response.data;

      setFormData((prev) => ({
        ...prev,
        full_name: parsed.full_name || prev.full_name,
        occupation: parsed.occupation || prev.occupation,
        yoe: parsed.yoe || prev.yoe,
        skills: parsed.skills ? parsed.skills.join(", ") : prev.skills,
      }));

      setStep(2);
    } catch (error) {
      console.error("CV Parse Error:", error);
      alert("Could not parse CV. Please enter data manually.");
    } finally {
      setUploading(false);
    }
  };

  const onDragOver = (e) => {
    e.preventDefault();
    setDragActive(true);
  };
  const onDragLeave = (e) => {
    e.preventDefault();
    setDragActive(false);
  };
  const onDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileUpload(e.dataTransfer.files[0]);
    }
  };

  // --- FORM SUBMIT ---
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = {
        ...formData,
        skills: formData.skills.split(",").map((s) => s.trim()),
        yoe: parseFloat(formData.yoe) || 0,
        monthly_income:
          employmentStatus === "employed"
            ? parseFloat(formData.monthly_income)
            : 0,
        savings_bracket:
          employmentStatus === "unemployed" ? formData.savings_amount : "0",
        is_unemployed: employmentStatus === "unemployed",
      };

      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/analysis/analyze`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setAnalysisResult(response.data);
      navigate("/results");
    } catch (error) {
      console.error("Error:", error);
      alert("Analysis failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const togglePriority = (priority) => {
    setFormData((prev) => ({
      ...prev,
      priorities: prev.priorities.includes(priority)
        ? prev.priorities.filter((p) => p !== priority)
        : [...prev.priorities, priority],
    }));
  };

  const PriorityCard = ({ id, label, icon: Icon, desc }) => (
    <div
      className={`priority-card ${
        formData.priorities.includes(id) ? "active" : ""
      }`}
      onClick={() => togglePriority(id)}
    >
      <div className="check-indicator"></div>
      <Icon className="p-icon" size={20} />
      <h3>{label}</h3>
      <p>{desc}</p>
    </div>
  );

  return (
    <div className="home-container">
      <div className="home-bg-glow" />

      <div className="dashboard-content">
        <div className="header-section">
          <div className="step-indicator">
            <span className={step >= 1 ? "active" : ""}>
              01. Identity Source
            </span>
            <span className="line"></span>
            <span className={step >= 2 ? "active" : ""}>02. Calibration</span>
          </div>
          <h1>{step === 1 ? "Initialize Identity" : "Confirm Profile Data"}</h1>
          <p>
            {step === 1
              ? "Upload your CV to auto-generate your mobility profile."
              : "Review your data. The algorithm adapts based on your employment status."}
          </p>
        </div>

        {step === 1 && (
          <div className="upload-section">
            <div
              className={`drop-zone ${dragActive ? "drag-active" : ""}`}
              onDragOver={onDragOver}
              onDragLeave={onDragLeave}
              onDrop={onDrop}
              onClick={() => fileInputRef.current.click()}
            >
              <input
                ref={fileInputRef}
                type="file"
                hidden
                accept=".pdf,.docx,.txt"
                onChange={(e) => handleFileUpload(e.target.files[0])}
              />

              {uploading ? (
                <div className="upload-status">
                  <Loader2 size={48} className="spinner icon-cyan" />
                  <h3>Analyzing Professional DNA...</h3>
                  <p>Extracting skills, seniority, and occupation.</p>
                </div>
              ) : (
                <div className="upload-content">
                  <div className="icon-circle">
                    <Upload size={32} />
                  </div>
                  <h3>Drop Resume / CV Here</h3>
                  <p>Supports PDF, DOCX, TXT</p>
                  <button className="secondary-btn">Or Browse Files</button>
                </div>
              )}
            </div>
            <div className="divider">
              <span>OR</span>
            </div>
            <button className="manual-btn" onClick={() => setStep(2)}>
              Enter Data Manually
            </button>
          </div>
        )}

        {step === 2 && (
          <form onSubmit={handleSubmit} className="config-grid slide-in">
            <div className="form-column">
              <div className="panel-group">
                <div className="status-selector">
                  <button
                    type="button"
                    className={`status-btn ${
                      employmentStatus === "employed" ? "active" : ""
                    }`}
                    onClick={() => setEmploymentStatus("employed")}
                  >
                    <Briefcase size={16} /> Employed
                  </button>
                  <button
                    type="button"
                    className={`status-btn ${
                      employmentStatus === "unemployed" ? "active" : ""
                    }`}
                    onClick={() => setEmploymentStatus("unemployed")}
                  >
                    <GraduationCap size={16} /> Open to Work
                  </button>
                </div>
                <p className="status-hint">
                  {employmentStatus === "employed"
                    ? "We will optimize for Digital Nomad & Remote Work visas."
                    : "We will optimize for Job Seeker Visas & Points-Based Immigration."}
                </p>
              </div>

              <div className="panel-group">
                <div className="panel-header">
                  <User size={18} /> <span>Identity Profile</span>
                </div>
                <div className="input-field">
                  <label>Full Name</label>
                  <div className="field-wrapper">
                    <User size={16} />
                    <input
                      type="text"
                      value={formData.full_name}
                      onChange={(e) =>
                        setFormData({ ...formData, full_name: e.target.value })
                      }
                      required
                    />
                  </div>
                </div>
                <div className="input-row">
                  <div className="input-field">
                    <label>Nationality</label>
                    <div className="field-wrapper">
                      <Globe size={16} />
                      <input
                        type="text"
                        value={formData.nationality}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            nationality: e.target.value,
                          })
                        }
                        required
                      />
                    </div>
                  </div>
                  <div className="input-field">
                    <label>Current Location</label>
                    <div className="field-wrapper">
                      <MapPin size={16} />
                      <input
                        type="text"
                        value={formData.current_country}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            current_country: e.target.value,
                          })
                        }
                        required
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="panel-group">
                <div className="panel-header">
                  <Briefcase size={18} /> <span>Professional Data</span>
                </div>
                <div className="input-row">
                  <div className="input-field">
                    <label>
                      {employmentStatus === "employed"
                        ? "Occupation"
                        : "Target Role"}
                    </label>
                    <div className="field-wrapper">
                      <Search size={16} />
                      <input
                        type="text"
                        value={formData.occupation}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            occupation: e.target.value,
                          })
                        }
                        required
                      />
                    </div>
                  </div>
                  <div className="input-field">
                    <label>Years Exp.</label>
                    <div className="field-wrapper">
                      <Clock size={16} />
                      <input
                        type="number"
                        value={formData.yoe}
                        onChange={(e) =>
                          setFormData({ ...formData, yoe: e.target.value })
                        }
                        required
                        min="0"
                        step="0.5"
                      />
                    </div>
                  </div>
                </div>
                <div className="input-field">
                  <label>Skills</label>
                  <div className="field-wrapper">
                    <Zap size={16} />
                    <input
                      type="text"
                      value={formData.skills}
                      onChange={(e) =>
                        setFormData({ ...formData, skills: e.target.value })
                      }
                      required
                    />
                  </div>
                </div>
                <div className="input-field">
                  <label>
                    {employmentStatus === "employed"
                      ? "Monthly Remote Income (USD)"
                      : "Liquid Savings (USD)"}
                  </label>
                  <div className="field-wrapper">
                    {employmentStatus === "employed" ? (
                      <DollarSign size={16} />
                    ) : (
                      <Wallet size={16} />
                    )}
                    <input
                      type="number"
                      value={
                        employmentStatus === "employed"
                          ? formData.monthly_income
                          : formData.savings_amount
                      }
                      onChange={(e) => {
                        const val = e.target.value;
                        if (employmentStatus === "employed")
                          setFormData({ ...formData, monthly_income: val });
                        else setFormData({ ...formData, savings_amount: val });
                      }}
                      required
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="form-column">
              <div className="panel-group">
                <div className="toggle-row">
                  <div className="toggle-label">
                    <Users size={18} />
                    <div>
                      <span>Relocating with Family?</span>
                    </div>
                  </div>
                  <label className="switch">
                    <input
                      type="checkbox"
                      checked={formData.has_family}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          has_family: e.target.checked,
                        })
                      }
                    />
                    <span className="slider round"></span>
                  </label>
                </div>
                {formData.has_family && (
                  <div className="input-field slide-in">
                    <label>Family Size</label>
                    <div className="field-wrapper">
                      <input
                        type="number"
                        value={formData.family_size}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            family_size: parseInt(e.target.value),
                          })
                        }
                        min="2"
                      />
                    </div>
                  </div>
                )}
              </div>

              <div className="panel-group full-height">
                <div className="panel-header">
                  <TrendingUp size={18} /> <span>Optimization Targets</span>
                </div>
                <div className="priority-grid">
                  <PriorityCard
                    id="cost"
                    label="Savings"
                    icon={DollarSign}
                    desc="Low tax & costs."
                  />
                  <PriorityCard
                    id="quality_of_life"
                    label="Lifestyle"
                    icon={Heart}
                    desc="Safety & vibes."
                  />
                  <PriorityCard
                    id="ease_of_visa"
                    label="Speed"
                    icon={Zap}
                    desc="Fast processing."
                  />
                  <PriorityCard
                    id="career_growth"
                    label="Tech Hubs"
                    icon={Briefcase}
                    desc="Jobs & network."
                  />
                </div>
                <button type="submit" className="launch-btn" disabled={loading}>
                  {loading ? (
                    <>
                      <Loader2 className="spinner" /> Processing...
                    </>
                  ) : (
                    <>
                      Run Analysis <TrendingUp size={18} />
                    </>
                  )}
                </button>
                <button
                  type="button"
                  className="text-btn"
                  onClick={() => setStep(1)}
                >
                  ← Re-upload CV
                </button>
              </div>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

export default Home;